import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { notifyFeedback } from '../api/client';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { ArrowLeft, ImagePlus, X, CheckCircle } from 'lucide-react';

type Category = 'Bug Report' | 'Feature Request' | 'General';
const CATEGORIES: Category[] = ['Bug Report', 'Feature Request', 'General'];
const MAX_IMAGES = 3;

interface ImageFile {
  file: File;
  preview: string;
}

export function FeedbackPage() {
  useDocumentTitle('Send Feedback');
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [category, setCategory] = useState<Category>('General');
  const [message, setMessage] = useState('');
  const [images, setImages] = useState<ImageFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const remaining = MAX_IMAGES - images.length;
    const selected = Array.from(files).slice(0, remaining);

    const newImages = selected.map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setImages(prev => [...prev, ...newImages]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index: number) => {
    setImages(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Strip the data:...;base64, prefix
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;

    setSubmitting(true);
    setError('');

    try {
      const attachments: { filename: string; contentType: string; data: string }[] = [];
      for (const img of images) {
        try {
          const base64 = await readFileAsBase64(img.file);
          attachments.push({
            filename: img.file.name || 'screenshot.jpg',
            contentType: img.file.type || 'image/jpeg',
            data: base64,
          });
        } catch {
          // Skip unreadable files
        }
      }

      Sentry.captureFeedback(
        {
          name: user?.username || 'Anonymous',
          email: user?.email || '',
          message: `[${category}] ${message}`,
        },
        {
          captureContext: (scope) => {
            scope.setTag('feedback_category', category);
            scope.setTag('username', user?.username || 'unknown');
            scope.setTag('source', 'web');
            for (const attachment of attachments) {
              scope.addAttachment(attachment);
            }
            return scope;
          },
        }
      );

      // Also send email notification (fire-and-forget)
      notifyFeedback(category, message, user?.username || 'Anonymous', user?.email || '').catch(() => {});

      setSuccess(true);
      setMessage('');
      setImages([]);
      setCategory('General');
    } catch {
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.content}>
          <button style={styles.backButton} onClick={() => navigate('/account')}>
            <ArrowLeft size={18} /> Back to Account
          </button>

          <h1 style={styles.title}>Send Feedback</h1>

          {success ? (
            <div style={styles.successCard}>
              <CheckCircle size={32} color={colors.status.success} />
              <h2 style={styles.successTitle}>Thank you!</h2>
              <p style={styles.successText}>Your feedback has been submitted successfully.</p>
              <button style={styles.successButton} onClick={() => setSuccess(false)}>
                Send more feedback
              </button>
            </div>
          ) : (
            <>
              {/* Category */}
              <div style={styles.section}>
                <label style={styles.label}>Category</label>
                <div style={styles.categoryRow}>
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setCategory(cat)}
                      style={{
                        ...styles.categoryPill,
                        ...(category === cat ? styles.categoryPillActive : {}),
                      }}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div style={styles.section}>
                <label style={styles.label}>Message</label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Tell us what's on your mind..."
                  style={styles.textarea}
                  rows={5}
                />
              </div>

              {/* Screenshots */}
              <div style={styles.section}>
                <label style={styles.label}>Screenshots (optional)</label>
                <div style={styles.imageRow}>
                  {images.map((img, i) => (
                    <div key={img.preview} style={styles.imageThumb}>
                      <img src={img.preview} alt={`Screenshot ${i + 1}`} style={styles.thumbImg} />
                      <button onClick={() => removeImage(i)} style={styles.removeBtn}>
                        <X size={10} color="#fff" />
                      </button>
                    </div>
                  ))}
                  {images.length < MAX_IMAGES && (
                    <button onClick={() => fileInputRef.current?.click()} style={styles.addImageBtn}>
                      <ImagePlus size={22} color={colors.gray[400]} />
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>

              {/* Error */}
              {error && <p style={styles.error}>{error}</p>}

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={submitting || !message.trim()}
                style={{
                  ...styles.submitBtn,
                  opacity: submitting || !message.trim() ? 0.5 : 1,
                  cursor: submitting || !message.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
  },
  content: {
    maxWidth: '600px',
    margin: '0 auto',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.gray[600],
    fontSize: 14,
    fontWeight: 500,
    padding: 0,
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.ink.black,
    marginBottom: 24,
  },
  section: {
    marginBottom: 24,
  },
  label: {
    display: 'block',
    fontSize: 14,
    fontWeight: 600,
    color: colors.ink.black,
    marginBottom: 8,
  },
  categoryRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  categoryPill: {
    padding: '8px 16px',
    borderRadius: 20,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.gray[300],
    background: colors.white,
    fontSize: 14,
    fontWeight: 500,
    color: colors.ink.black,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  categoryPillActive: {
    background: colors.brand.blue,
    borderColor: colors.brand.blue,
    color: '#fff',
  },
  textarea: {
    width: '100%',
    padding: 14,
    fontSize: 15,
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: 12,
    color: colors.ink.black,
    resize: 'vertical' as const,
    fontFamily: 'inherit',
    outline: 'none',
    minHeight: 120,
    boxSizing: 'border-box' as const,
  },
  imageRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  imageThumb: {
    position: 'relative' as const,
    width: 80,
    height: 80,
    borderRadius: 10,
    overflow: 'hidden',
    border: `1px solid ${colors.gray[200]}`,
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
  },
  removeBtn: {
    position: 'absolute' as const,
    top: 4,
    right: 4,
    width: 20,
    height: 20,
    borderRadius: 10,
    background: 'rgba(0,0,0,0.7)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  addImageBtn: {
    width: 80,
    height: 80,
    borderRadius: 10,
    border: `1.5px dashed ${colors.gray[300]}`,
    background: colors.gray[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  error: {
    fontSize: 14,
    color: '#DC2626',
    marginBottom: 16,
  },
  submitBtn: {
    width: '100%',
    padding: 16,
    borderRadius: 12,
    background: colors.brand.blue,
    border: 'none',
    fontSize: 16,
    fontWeight: 600,
    color: '#fff',
    transition: 'opacity 0.2s',
  },
  successCard: {
    textAlign: 'center' as const,
    padding: '48px 24px',
    backgroundColor: colors.white,
    borderRadius: 16,
    border: `1px solid ${colors.gray[300]}`,
  },
  successTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: colors.ink.black,
    marginTop: 16,
    marginBottom: 8,
  },
  successText: {
    fontSize: 14,
    color: colors.gray[600],
    marginBottom: 24,
  },
  successButton: {
    padding: '10px 20px',
    borderRadius: 8,
    border: `1px solid ${colors.gray[300]}`,
    background: colors.white,
    fontSize: 14,
    fontWeight: 500,
    color: colors.ink.black,
    cursor: 'pointer',
  },
};
